import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Linking, Platform, Text, View } from 'react-native';

import {
	EMAIL_FRAME_MESSAGE,
	EMAIL_FRAME_MIN_HEIGHT,
	buildEmailDocument,
	clampFrameHeight,
} from '@/utils/email-html';
import { linkifySegments } from '@/utils/linkify';

export interface HtmlBodyProps {
	/** Raw inbound email HTML. Sanitized before it is rendered. */
	html?: string | null;
	/** Plain-text rendering used whenever the HTML can't be shown. */
	fallbackText?: string | null;
	/** Wording when there is neither HTML nor text; each surface phrases this its own way. */
	emptyText?: string;
	testID?: string;
}

const EMPTY_NOTICE = 'This email has no body text.';

/**
 * Renders an inbound email body (#363).
 *
 * On web the HTML goes into a sandboxed `<iframe srcdoc>`. The sandbox omits
 * `allow-same-origin`, so the frame runs in an opaque origin: script inside it
 * cannot read the device token, cookies, or any app state.
 *
 * The sandbox does NOT, however, contain URL resolution — a `srcdoc` document
 * resolves relative references against the PARENT document's URL, and
 * `allow-popups-to-escape-sandbox` means a `target="_blank"` click opens an
 * UNSANDBOXED tab. So a relative `href` would open the real app origin, fully
 * authenticated. That is closed in `sanitizeEmailHtml`, which requires an absolute
 * allow-listed scheme on every URL. The two layers cover different things.
 *
 * `allow-scripts` is present only so OUR injected height reporter can measure the
 * content — the email's own scripts are stripped before the document is assembled.
 *
 * On native this renders the plain-text fallback. Swapping in
 * `react-native-webview` is the deferred follow-up; this component is the seam.
 */
export const HtmlBody: React.FC<HtmlBodyProps> = ({ html, fallbackText, emptyText, testID }) => {
	const iframeRef = useRef<HTMLIFrameElement | null>(null);
	const [height, setHeight] = useState(EMAIL_FRAME_MIN_HEIGHT);

	// Empty output means nothing survived sanitizing — fall back rather than show
	// an empty frame. Memoized so a re-render doesn't re-parse the whole document.
	const document_ = useMemo(
		() => (Platform.OS === 'web' ? buildEmailDocument(html) : ''),
		[html],
	);

	useEffect(() => {
		if (!document_) return undefined;
		const onMessage = (event: MessageEvent) => {
			// Only this frame may resize itself; ignore every other window's traffic.
			if (event.source !== iframeRef.current?.contentWindow) return;
			const data = event.data;
			if (!data || data.type !== EMAIL_FRAME_MESSAGE) return;
			setHeight(clampFrameHeight(data.height));
		};
		window.addEventListener('message', onMessage);
		return () => window.removeEventListener('message', onMessage);
	}, [document_]);

	if (!document_) {
		const text = fallbackText?.trim();
		if (!text) {
			return <Text testID={testID} style={{ lineHeight: 20 }}>{emptyText || EMPTY_NOTICE}</Text>;
		}
		/**
		 * Plain text still gets working links. Forwarded staff mail and text/plain form
		 * notifications carry the Webflow upload URLs, and as bare characters the only way
		 * to follow one is to select it by hand.
		 *
		 * Nested `<Text onPress>` rather than a Pressable per link: a Pressable is a block
		 * box on RN-Web and would break every linked URL onto its own line mid-sentence.
		 */
		return (
			<Text testID={testID} style={{ lineHeight: 20 }} selectable>
				{linkifySegments(text).map((segment, i) => (
					segment.kind === 'link' ? (
						<Text
							key={i}
							style={{ color: '#1565c0', textDecorationLine: 'underline' }}
							onPress={() => { void Linking.openURL(segment.href); }}
						>
							{segment.value}
						</Text>
					) : segment.value
				))}
			</Text>
		);
	}

	return (
		<View testID={testID} style={{ width: '100%' }}>
			<iframe
				ref={iframeRef}
				title="Email message"
				srcDoc={document_}
				sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox"
				style={{ width: '100%', height, border: 'none', display: 'block' }}
			/>
		</View>
	);
};
